import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const [showOriginalDetails, setShowOriginalDetails] = useState(false);
  const [showChiliDetails, setShowChiliDetails] = useState(false);
  const [showWholesaleInquiry, setShowWholesaleInquiry] = useState(false);
  const [inquiryFormData, setInquiryFormData] = useState({
    name: '',
    email: '',
    company: '',
    region: '',
    phone: '',
    message: ''
  });
  const [inquiryStatus, setInquiryStatus] = useState('');

  const handleInquiryChange = (e) => {
    setInquiryFormData({
      ...inquiryFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setInquiryStatus('sending');
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/wholesale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: inquiryFormData.company,
          contact_name: inquiryFormData.name,
          email: inquiryFormData.email,
          phone: inquiryFormData.phone,
          country: inquiryFormData.region,
          business_vision: inquiryFormData.message
        })
      });
      
      if (response.ok) {
        setInquiryStatus('success');
        setInquiryFormData({ name: '', email: '', company: '', region: '', phone: '', message: '' });
        setTimeout(() => setShowWholesaleInquiry(false), 3000);
      } else {
        setInquiryStatus('error');
      }
    } catch (error) {
      setInquiryStatus('error');
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Premium Australian Biltong"
        description="Australia's #1 premium biltong with 52% protein. 100% Australian beef, air-dried using a 200-year-old South African recipe. Australian Made certified. Free shipping over $59."
        keywords="biltong Australia, Australian biltong, buy biltong online, premium beef biltong, high protein snack, air-dried beef, Original Bull, best biltong Australia, protein snack"
        url="/"
      />
      <Header onShopNowClick={() => setShowWholesaleInquiry(true)} />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-4 text-sm tracking-[0.3em] text-gray-600 font-semibold">
            TRADITIONAL RECIPE • 1820
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            <span className="text-red-700">ORIGINAL BULL</span>
            <br />
            <span className="text-gray-900">BILTONG</span>
          </h1>
          <p className="text-xl text-gray-600 mb-3 italic">
            "The Original Recipe by the Original Bull"
          </p>
          <p className="text-lg text-gray-700 mb-5 max-w-2xl mx-auto">
            Two centuries of South African craftsmanship.<br />
            Premium Australian beef. Traditional methods. Uncompromising quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/shop')}
              className="bg-red-700 text-white px-8 py-3 rounded-md hover:bg-red-800 transition-colors font-medium text-lg text-center"
            >
              Shop Now
            </button>
            <button 
              onClick={() => setShowWholesaleInquiry(true)}
              className="border-2 border-red-700 text-red-700 px-8 py-3 rounded-md hover:bg-red-700 hover:text-white transition-colors font-medium text-lg text-center"
            >
              Wholesale Inquiry
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10 max-w-4xl mx-auto">
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">52%</div>
              <div className="text-gray-600">Protein</div>
              <div className="text-sm font-semibold text-red-600 mt-1">2X Steak Power!</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">100%</div>
              <div className="text-gray-600">Australian Beef</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">200</div>
              <div className="text-gray-600">Years Heritage</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">100%</div>
              <div className="text-gray-600">Natural</div>
            </div>
          </div>
          
          {/* Protein Power Banner */}
          <div className="mt-8 text-center">
            <div className="text-8xl font-black text-red-700 mb-2">2X</div>
            <div className="bg-red-700 text-white py-3 px-6 rounded-full inline-block">
              <span className="font-bold text-lg">DOUBLE THE PROTEIN OF STEAK - HALF THE SIZE!</span>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm tracking-[0.3em] text-gray-600 font-semibold mb-3">
              PREMIUM COLLECTION
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">OUR BILTONG</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Two distinctive varieties, each representing two centuries of South African tradition perfected with premium Australian beef.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Original Flavour */}
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow flex flex-col" data-testid="original-flavour-product">
              <div className="aspect-square overflow-hidden relative">
                <div className="absolute top-4 right-4 bg-amber-700 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                  Original
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_3ad0df18-66ae-47af-92fb-c4edc5379868/artifacts/z20lv4db_Original%20Flavour%20for%20the%20Website.jpg" 
                  alt="Original Flavour Biltong - Authentic Traditional South African Dried Meat" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 flex flex-col">
                <h3 className="text-2xl font-bold mb-3">ORIGINAL FLAVOUR BILTONG</h3>
                <p className="text-gray-600 mb-4 min-h-[3rem]">
                  Our signature 200-year-old recipe crafted from 100% Australian beef
                </p>
                
                <div className="flex justify-between px-8 mb-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">52%</div>
                    <div className="text-sm text-gray-600">Protein</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">&lt;5%</div>
                    <div className="text-sm text-gray-600">Fat</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">50g</div>
                    <div className="text-sm text-gray-600">Weight</div>
                  </div>
                </div>
                
                <div className="text-center mb-4">
                  <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold">
                    🥩 2X THE PROTEIN OF STEAK!
                  </span>
                </div>

                <div className="flex flex-wrap justify-center gap-2 mb-4 min-h-[2.5rem]">
                  <span className="px-3 py-1 bg-white rounded-full text-sm">100% Australian Beef</span>
                  <span className="px-3 py-1 bg-white rounded-full text-sm">Air-Dried</span>
                  <span className="px-3 py-1 bg-white rounded-full text-sm">Natural CoQ10</span>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <button 
                    onClick={() => navigate('/shop')}
                    className="flex-1 bg-red-700 text-white px-6 py-2 rounded-md hover:bg-red-800 transition-colors font-medium" 
                  >
                    Buy Now
                  </button>
                  <button 
                    onClick={() => setShowOriginalDetails(true)}
                    className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-md hover:border-red-700 hover:text-red-700 transition-colors" 
                    data-testid="select-original-btn"
                  >
                    Details
                  </button>
                </div>
              </div>
            </div>

            {/* Chilli Flavour */}
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow flex flex-col" data-testid="chili-flavour-product">
              <div className="aspect-square overflow-hidden relative">
                <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                  Spicy
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_3ad0df18-66ae-47af-92fb-c4edc5379868/artifacts/7kalijqo_Chilli%20Flavour%20for%20the%20Website.jpg" 
                  alt="Chilli Flavour Biltong - Authentic Spicy South African Dried Meat with Red Chilli Flakes" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 flex flex-col">
                <h3 className="text-2xl font-bold mb-3">CHILLI FLAVOUR BILTONG</h3>
                <p className="text-gray-600 mb-4 min-h-[3rem]">
                  Our signature 200-year-old recipe enhanced with a balanced spice blend
                </p>
                
                <div className="flex justify-between px-8 mb-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">52%</div>
                    <div className="text-sm text-gray-600">Protein</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">&lt;5%</div>
                    <div className="text-sm text-gray-600">Fat</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">50g</div>
                    <div className="text-sm text-gray-600">Weight</div>
                  </div>
                </div>
                
                <div className="text-center mb-4">
                  <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold">
                    🥩 2X THE PROTEIN OF STEAK!
                  </span>
                </div>

                <div className="flex flex-wrap justify-center gap-2 mb-4 min-h-[2.5rem]">
                  <span className="px-3 py-1 bg-white rounded-full text-sm">100% Australian Beef</span>
                  <span className="px-3 py-1 bg-white rounded-full text-sm">Air-Dried</span>
                  <span className="px-3 py-1 bg-white rounded-full text-sm">Natural CoQ10</span>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <button 
                    onClick={() => navigate('/shop')}
                    className="flex-1 bg-red-700 text-white px-6 py-2 rounded-md hover:bg-red-800 transition-colors font-medium" 
                  >
                    Buy Now
                  </button>
                  <button 
                    onClick={() => setShowChiliDetails(true)}
                    className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-md hover:border-red-700 hover:text-red-700 transition-colors" 
                    data-testid="select-chili-btn"
                  >
                    Details
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 mt-8 max-w-4xl mx-auto">
            Each package honours a 200-year South African heritage, where Dutch settlers perfected the art of biltong. We've brought this time-honoured tradition to Australia, crafting it with the world's finest beef to create something truly exceptional.
          </p>
        </div>
      </section>

      {/* CoQ10 Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm tracking-[0.3em] text-gray-600 font-semibold mb-3">
              NATURAL ENZYME ADVANTAGE
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">CoQ10 ENRICHED</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Unlike jerky's high-heat processing, our traditional air-drying method preserves the natural CoQ10 enzymes found in premium Australian beef, delivering exceptional health benefits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-10">
            {[
              { num: '1', title: 'Supports cellular energy', desc: 'Supports cellular energy production' },
              { num: '2', title: 'Powerful antioxidant', desc: 'Powerful antioxidant properties' },
              { num: '3', title: 'Promotes cardiovascular', desc: 'Promotes cardiovascular health' },
              { num: '4', title: 'Helps maintain', desc: 'Helps maintain healthy aging' },
              { num: '5', title: 'Supports muscle', desc: 'Supports muscle function and recovery' },
            ].map((item) => (
              <div key={item.num} className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-red-700 mb-3">{item.num}</div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg">
            <h3 className="text-2xl font-bold mb-4">THE DIFFERENCE IS IN THE PROCESS</h3>
            <p className="text-gray-600 mb-5">
              Commercial jerky production uses high temperatures that destroy natural enzymes and nutrients. Our 200-year-old air-drying tradition preserves these vital compounds, making our biltong a superior nutritional choice.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="border-2 border-green-500 rounded-xl p-6">
                <h4 className="font-bold text-lg mb-4 text-green-700">BILTONG PROCESS</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✓ Air-dried at room temperature</li>
                  <li>✓ Enzymes preserved</li>
                  <li>✓ Natural nutrients retained</li>
                  <li>✓ Maximum bioavailability</li>
                </ul>
              </div>
              <div className="border-2 border-red-500 rounded-xl p-6">
                <h4 className="font-bold text-lg mb-4 text-red-700">JERKY PROCESS</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>✗ Heat-processed at 160°F+</li>
                  <li>✗ Enzymes destroyed</li>
                  <li>✗ Nutrients degraded</li>
                  <li>✗ Reduced nutritional value</li>
                </ul>
              </div>
            </div>

            <p className="text-center text-gray-700 mt-8 font-medium">
              Experience the nutritional superiority of authentic biltong and taste the difference that 200 years of South African tradition makes, now crafted with Australia's finest beef.
            </p>
          </div>
        </div>
      </section>

      {/* Heritage Section */}
      <section id="heritage" className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm tracking-[0.3em] text-gray-400 font-semibold mb-4">
              HERITAGE & TRADITION
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">200 YEARS</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Passed down through generations, our biltong recipe originated in the early 1800s when Dutch settlers perfected the art of preserving meat in South Africa's harsh climate. South Africans later brought this cherished tradition to Australia, discovering that Australia's premium beef was ideally suited to this time-honored craft.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            <div className="text-center p-8 bg-gray-800 rounded-xl">
              <div className="text-5xl font-bold text-red-600 mb-4">1820</div>
              <h3 className="text-xl font-bold mb-3">RECIPE ORIGINS</h3>
              <p className="text-gray-400">
                Dutch settlers in South Africa perfected the art of preserving meat in harsh climates, creating the foundation of our time-honored biltong recipe.
              </p>
            </div>
            <div className="text-center p-8 bg-gray-800 rounded-xl">
              <div className="text-5xl font-bold text-red-600 mb-4">1850s</div>
              <h3 className="text-xl font-bold mb-3">SPICE MASTERY</h3>
              <p className="text-gray-400">
                Generations of craftsmen refine the spice blend, creating the distinctive flavour profiles that define our biltong today.
              </p>
            </div>
            <div className="text-center p-8 bg-gray-800 rounded-xl">
              <div className="text-5xl font-bold text-red-600 mb-4">2019</div>
              <h3 className="text-xl font-bold mb-3">AUSTRALIAN EXCELLENCE</h3>
              <p className="text-gray-400">
                Bringing this South African tradition to Australia, we craft authentic biltong using premium Australian beef and centuries-old techniques.
              </p>
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold mb-8 text-center">OUR PROCESS</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                'Selection of premium Australian beef cuts',
                'Hand-cut into traditional strips',
                'Cured with authentic spice blend',
                'Air-dried using traditional methods',
                'Quality tested for perfect texture',
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-red-700 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                    {index + 1}
                  </div>
                  <p className="text-sm text-gray-300">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            <div className="text-center p-6 bg-gray-800 rounded-xl">
              <div className="text-3xl mb-2">🌿</div>
              <div className="font-bold mb-1">NATURAL</div>
              <div className="text-sm text-gray-400">100% Pure</div>
            </div>
            <div className="text-center p-6 bg-gray-800 rounded-xl">
              <div className="text-3xl mb-2">✋</div>
              <div className="font-bold mb-1">HANDCRAFTED</div>
              <div className="text-sm text-gray-400">Artisan Made</div>
            </div>
            <div className="text-center p-6 bg-gray-800 rounded-xl">
              <div className="text-3xl mb-2">🥩</div>
              <div className="font-bold mb-1">PREMIUM</div>
              <div className="text-sm text-gray-400">Australian Beef</div>
            </div>
            <div className="text-center p-6 bg-gray-800 rounded-xl">
              <div className="text-3xl mb-2">✓</div>
              <div className="font-bold mb-1">LICENSED</div>
              <div className="text-sm text-gray-400">Australian Made</div>
            </div>
          </div>

          <p className="text-center text-gray-400 mt-8">
            Original Bull is the first and only biltong producer to hold official Australian Made certification.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm tracking-[0.3em] text-gray-600 font-semibold mb-4">
              TESTIMONIALS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">CUSTOMERS</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {[
              {
                text: 'The best biltong I\'ve ever tasted! The texture is perfect and the flavour is incredible. You can really taste the quality of the Australian beef.',
                name: 'Sarah Chen',
                location: 'Sydney, NSW',
              },
              {
                text: 'As someone who grew up in South Africa, I was skeptical about Australian biltong. Original Bull has completely changed my mind - this is authentic!',
                name: 'Michael Johnson',
                location: 'Melbourne, VIC',
              },
              {
                text: 'Perfect protein snack for my workouts. Love that it\'s low in fat but packed with protein. The chilli flavour has the perfect kick!',
                name: 'Emma Rodriguez',
                location: 'Brisbane, QLD',
              },
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl shadow-sm">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-12 text-center">
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">4.9</div>
              <div className="text-gray-600">Average Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">100+</div>
              <div className="text-gray-600">Reviews</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-700 mb-2">98%</div>
              <div className="text-gray-600">Recommend</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm tracking-[0.3em] text-gray-600 font-semibold mb-4">
              CONTACT
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">GET IN TOUCH</h2>
            <p className="text-gray-600 italic">"The Original Recipe by the Original Bull"</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ContactForm />

            <div className="space-y-5">
              <div>
                <h3 className="text-xl font-bold mb-4">VISIT US</h3>
                <p className="text-gray-700">Southern Highlands, NSW</p>
                <p className="text-gray-700">Australia</p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">CONTACT</h3>
                <p className="text-gray-700">info@originalbull.com</p>
                <p className="text-gray-700">+61 2 9037 3883</p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">HOURS</h3>
                <p className="text-gray-700">Monday – Friday</p>
                <p className="text-gray-700">9:00 AM – 5:00 PM</p>
                <p className="text-gray-600 text-sm mt-2">Weekend orders processed Monday</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wholesale Inquiry Modal */}
      {showWholesaleInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowWholesaleInquiry(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-3xl font-bold text-red-700">Wholesale Inquiry</h2>
                <button onClick={() => setShowWholesaleInquiry(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-700 p-4 mb-6">
                <p className="text-gray-800 font-medium mb-2">
                  Premium Wholesale Product
                </p>
                <p className="text-gray-700 text-sm">
                  Original Bull Biltong is exclusively available through our wholesale and distribution partners. We work with businesses across Australia and internationally to bring authentic, premium-quality biltong to discerning customers. Please complete the inquiry form below, and our regional distribution team will contact you within 24 hours to discuss partnership opportunities, pricing, and delivery options tailored to your location.
                </p>
              </div>

              {inquiryStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  Thank you! Your inquiry has been received. Our team will contact you within 24 hours.
                </div>
              )}
              {inquiryStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  Sorry, there was an error. Please email us directly at info@originalbull.com
                </div>
              )}

              <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={inquiryFormData.name}
                      onChange={handleInquiryChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={inquiryFormData.email}
                      onChange={handleInquiryChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input
                      type="text"
                      name="company"
                      value={inquiryFormData.company}
                      onChange={handleInquiryChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={inquiryFormData.phone}
                      onChange={handleInquiryChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Region/Country *</label>
                  <input
                    type="text"
                    name="region"
                    value={inquiryFormData.region}
                    onChange={handleInquiryChange}
                    required
                    placeholder="e.g., Sydney NSW, New Zealand, Singapore"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Inquiry</label>
                  <textarea
                    name="message"
                    value={inquiryFormData.message}
                    onChange={handleInquiryChange}
                    rows="4"
                    placeholder="Please tell us about your interest in Original Bull Biltong - quantity needed, intended use, distribution plans, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                  ></textarea>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={inquiryStatus === 'sending'}
                    className="flex-1 bg-red-700 text-white px-6 py-3 rounded-md hover:bg-red-800 transition-colors font-medium disabled:bg-gray-400"
                  >
                    {inquiryStatus === 'sending' ? 'Sending...' : 'Submit Inquiry'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowWholesaleInquiry(false)}
                    className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:border-red-700 hover:text-red-700 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Original Flavour Details Modal */}
      {showOriginalDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowOriginalDetails(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-3xl font-bold">ORIGINAL FLAVOUR BILTONG</h2>
                <button onClick={() => setShowOriginalDetails(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
              </div>
              
              <p className="text-gray-700 mb-6">Our signature 200-year-old recipe crafted from 100% Australian beef</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-xl mb-3">Product Details</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Weight: 50g</li>
                    <li>• Protein: 52%</li>
                    <li>• Fat: Less than 5%</li>
                    <li>• 100% Australian Beef</li>
                    <li>• Air-Dried using traditional methods</li>
                    <li>• Contains Natural CoQ10</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-bold text-xl mb-3">Ingredients</h3>
                  <p className="text-gray-700">Beef (100% Australian), Salt, Coriander, Black Pepper, Vinegar (Preservative), Brown Sugar</p>
                </div>
                
                <div>
                  <h3 className="font-bold text-xl mb-3">Nutritional Information (per 50g serving)</h3>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Energy: 520kJ</li>
                    <li>• Protein: 26g</li>
                    <li>• Fat, total: 2.5g</li>
                    <li>• Carbohydrate: 1g</li>
                    <li>• Sodium: 800mg</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-bold text-xl mb-3">Storage</h3>
                  <p className="text-gray-700">Store in a cool, dry place. Once opened, consume within 7 days for best quality.</p>
                </div>
              </div>
              
              <div className="mt-8 flex gap-4">
                <a href="mailto:info@originalbull.com?subject=Order%20Original%20Flavour%20Biltong" className="flex-1 bg-red-700 text-white px-6 py-3 rounded-md hover:bg-red-800 transition-colors text-center font-medium">
                  Order Now
                </a>
                <button onClick={() => setShowOriginalDetails(false)} className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:border-red-700 hover:text-red-700 transition-colors font-medium">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chilli Flavour Details Modal */}
      {showChiliDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowChiliDetails(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-3xl font-bold">CHILLI FLAVOUR BILTONG</h2>
                <button onClick={() => setShowChiliDetails(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
              </div>
              
              <p className="text-gray-700 mb-6">Our time-honored recipe enhanced with a carefully balanced spice blend for those who appreciate depth and warmth</p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-xl mb-3">Product Details</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Weight: 50g</li>
                    <li>• Protein: 52%</li>
                    <li>• Fat: Less than 5%</li>
                    <li>• 100% Australian Beef</li>
                    <li>• Air-Dried using traditional methods</li>
                    <li>• Contains Natural CoQ10</li>
                    <li>• 🌶️ Spicy Heat Level: Medium-Hot</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-bold text-xl mb-3">Ingredients</h3>
                  <p className="text-gray-700">Beef (100% Australian), Salt, Coriander, Black Pepper, Chilli Flakes, Cayenne Pepper, Paprika, Vinegar (Preservative), Brown Sugar</p>
                </div>
                
                <div>
                  <h3 className="font-bold text-xl mb-3">Nutritional Information (per 50g serving)</h3>
                  <ul className="space-y-1 text-gray-700">
                    <li>• Energy: 520kJ</li>
                    <li>• Protein: 26g</li>
                    <li>• Fat, total: 2.5g</li>
                    <li>• Carbohydrate: 1g</li>
                    <li>• Sodium: 850mg</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-bold text-xl mb-3">Storage</h3>
                  <p className="text-gray-700">Store in a cool, dry place. Once opened, consume within 7 days for best quality.</p>
                </div>
              </div>
              
              <div className="mt-8 flex gap-4">
                <a href="mailto:info@originalbull.com?subject=Order%20Chilli%20Flavour%20Biltong" className="flex-1 bg-red-700 text-white px-6 py-3 rounded-md hover:bg-red-800 transition-colors text-center font-medium">
                  Order Now
                </a>
                <button onClick={() => setShowChiliDetails(false)} className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:border-red-700 hover:text-red-700 transition-colors font-medium">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Home;